/**
 * Phase 10c — Historique action sur une facture fournisseur (mini timeline verticale).
 *
 * Affiche jusqu'à 3 étapes chronologiques :
 *   1. Reçue le X via {source}
 *   2. Validée par Y le Z (si validated_at)
 *   3. Payée par W le V (si paid_at)
 *
 * Format short fr-BE `05/09/2026 · 14h30`.
 * Résolution des noms via `validated_by_name` / `paid_by_name` peuplés par le backend.
 */
import { CheckCircle2, CircleDot, Wallet, Inbox } from "lucide-react";

function shortDateTimeBE(iso) {
  if (!iso) return "—";
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return "—";
  const date = dt.toLocaleDateString("fr-BE", { day: "2-digit", month: "2-digit", year: "numeric" });
  const h = String(dt.getHours()).padStart(2, "0");
  const m = String(dt.getMinutes()).padStart(2, "0");
  return `${date} · ${h}h${m}`;
}

export default function SupplierInvoiceHistory({ invoice }) {
  const steps = [
    {
      key: "received",
      label: "Reçue",
      icon: Inbox,
      when: invoice.received_at,
      who: invoice.source === "peppol_inbound" ? "via Peppol (MOCK)" : `via ${invoice.source || "manuel"}`,
      done: true,
    },
    {
      key: "validated",
      label: "Validée",
      icon: CheckCircle2,
      when: invoice.validated_at,
      who: invoice.validated_by_name ? `par ${invoice.validated_by_name}` : null,
      done: !!invoice.validated_at,
    },
    {
      key: "paid",
      label: "Payée",
      icon: Wallet,
      when: invoice.paid_at,
      who: invoice.paid_by_name ? `par ${invoice.paid_by_name}` : null,
      done: !!invoice.paid_at,
    },
  ];

  return (
    <div
      className="rounded border border-slate-800 bg-slate-950/40 p-3"
      data-testid="supplier-invoice-history"
    >
      <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
        Historique
      </div>
      <ol className="space-y-3">
        {steps.map((s) => {
          const Icon = s.icon;
          const active = s.done;
          return (
            <li
              key={s.key}
              className="flex gap-3"
              data-testid={`history-step-${s.key}`}
            >
              <div className="flex flex-col items-center">
                <div
                  className={`flex h-6 w-6 items-center justify-center rounded-full border ${
                    active
                      ? "border-emerald-500/60 bg-emerald-500/20 text-emerald-300"
                      : "border-slate-700 bg-slate-800/50 text-slate-500"
                  }`}
                >
                  {active ? (
                    <Icon className="h-3 w-3" />
                  ) : (
                    <CircleDot className="h-3 w-3" />
                  )}
                </div>
              </div>
              <div className="flex-1 pb-1">
                <div className={`text-sm font-medium ${active ? "text-slate-100" : "text-slate-500"}`}>
                  {s.label}
                  {!active && <span className="ml-1 text-xs italic">— en attente</span>}
                </div>
                {active && (
                  <div className="text-xs text-slate-400">
                    {shortDateTimeBE(s.when)}
                    {s.who && <span className="text-slate-500"> · {s.who}</span>}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
